import React, { useState, useEffect } from 'react';
import StatCard from '../../components/admin/StatCard';
import ActivityChart from '../../components/admin/ActivityChart';
import RecentMessages from '../../components/admin/RecentMessages';
import { useTheme } from '../../contexts/ThemeContext';
import { DashboardData, ConnectionStatus } from '../../types/admin';
import { adminApi } from '../../services/admin/adminApi';

const Dashboard: React.FC = () => {
  const { isDark } = useTheme();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();

    // Refresh data every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const result = await adminApi.getDashboard();
      setData(result);
      setError(null);
    } catch (err) {
      // Use mock data for now if API not available
      setData({
        stats: {
          messagesToday: 42,
          activeChats: 8,
          responseRate: 94,
          connectionStatus: 'connected' as ConnectionStatus,
        },
        recentMessages: [
          {
            id: '1',
            contact: 'Joao Silva',
            phone: '+55 11 99999-1234',
            message: 'Ola, gostaria de fazer uma cotacao de seguro',
            type: 'text',
            status: 'received',
            timestamp: new Date().toISOString(),
          },
          {
            id: '2',
            contact: 'Maria Santos',
            phone: '+55 21 98888-5678',
            message: 'Qual o valor do seguro para um Civic 2022?',
            type: 'text',
            status: 'received',
            timestamp: new Date(Date.now() - 300000).toISOString(),
          },
          {
            id: '3',
            contact: 'Pedro Costa',
            phone: '+55 31 97777-9012',
            message: 'Obrigado pela cotacao!',
            type: 'text',
            status: 'sent',
            timestamp: new Date(Date.now() - 600000).toISOString(),
          },
        ],
        hourlyActivity: Array.from({ length: 24 }, () => Math.floor(Math.random() * 20)),
      });
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const getConnectionStatusBadge = (status: ConnectionStatus) => {
    const statusConfig = {
      connected: { color: 'bg-green-500', text: 'Conectado' },
      connecting: { color: 'bg-yellow-500', text: 'Conectando...' },
      disconnected: { color: 'bg-red-500', text: 'Desconectado' },
      qr: { color: 'bg-blue-500', text: 'Aguardando QR' },
      error: { color: 'bg-red-500', text: 'Erro' },
    };

    const config = statusConfig[status] || statusConfig.disconnected;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white ${config.color}`}>
        <span className="w-2 h-2 rounded-full bg-white mr-2 animate-pulse"></span>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Dashboard
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Visao geral do sistema WhatsApp
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          {data && getConnectionStatusBadge(data.stats.connectionStatus)}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Status da Conexao"
          value={data?.stats.connectionStatus === 'connected' ? 'Online' : 'Offline'}
          color={data?.stats.connectionStatus === 'connected' ? 'success' : 'danger'}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
          }
        />
        <StatCard
          title="Mensagens Hoje"
          value={data?.stats.messagesToday || 0}
          color="success"
          trend={{ value: 12, isPositive: true }}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
        <StatCard
          title="Conversas Ativas"
          value={data?.stats.activeChats || 0}
          color="info"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
        <StatCard
          title="Taxa de Resposta"
          value={`${data?.stats.responseRate || 0}%`}
          color="warning"
          trend={{ value: 3, isPositive: true }}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
        />
      </div>

      {/* Charts and messages */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentMessages messages={data?.recentMessages || []} />
        </div>
        <div>
          <ActivityChart data={data?.hourlyActivity || Array(24).fill(0)} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
